// CEO Service for API communication
interface CeoData {
  id: number;
  type: string;
  title: string;
  content: string;
  icon: string | null;
  link: string | null;
  ceo_name: string;
  ceo_position: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface CeoResponse {
  success: boolean;
  data: CeoData | null;
  message?: string;
}

interface CeoUpdateResponse {
  success: boolean;
  message: string;
  data: CeoData;
}

class CeoService {
  private baseUrl: string;
  private lastRequestTime: number = 0;
  private minRequestInterval: number = 1000; // Minimum 1 second between requests

  constructor() {
    // Use mock API for development, production API when database is connected
    // With proxy configuration, we can use relative URLs
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
    console.log('CEO Service initialized with baseUrl:', this.baseUrl); // Debug log
  }

  /**
   * Get current CEO data with rate limiting protection
   */
  async getCeo(): Promise<CeoResponse> {
    try {
      // Check rate limiting
      const now = Date.now();
      if (now - this.lastRequestTime < this.minRequestInterval) {
        console.log('CEO request throttled to prevent rate limiting');
        // Return cached data or wait
        await new Promise(resolve => setTimeout(resolve, this.minRequestInterval - (now - this.lastRequestTime)));
      }
      
      this.lastRequestTime = Date.now();
      const response = await fetch(`${this.baseUrl}/static-data/ceo`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching CEO data:', error);
      return {
        success: false,
        data: null,
        message: 'Failed to fetch CEO data'
      };
    }
  }

  /**
   * Update CEO data with optional image upload
   * Creates new CEO if none exists
   */
  async updateCeo(
    title: string,
    content: string,
    ceoName: string,
    ceoPosition: string,
    imageFile?: File
  ): Promise<CeoUpdateResponse> {
    try {
      const formData = new FormData();
      
      formData.append('title', title);
      formData.append('content', content);
      formData.append('ceo_name', ceoName);
      formData.append('ceo_position', ceoPosition);
      
      if (imageFile) {
        formData.append('ceo_image', imageFile);
      }

      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/static-data/ceo`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData // Don't set Content-Type header, let browser set it for FormData
      });

      const data = await response.json();
      
      // Log the response for debugging
      console.log('CEO API Response:', data);
      
      return data;
    } catch (error) {
      console.error('Error updating CEO data:', error);
      return {
        success: false,
        message: 'Failed to update CEO data',
        data: {} as CeoData
      };
    }
  }

  /**
   * Get image URL for CEO photo
   */
  getImageUrl(iconPath: string | null): string {
    if (!iconPath) {
      return '/assets/images/md-ceo.svg'; // Default fallback image
    }
    
    // If it's already a full URL, return as is
    if (iconPath.startsWith('http')) {
      return iconPath;
    }
    
    // If it's a relative path starting with /uploads, return as relative URL (proxy will handle it)
    if (iconPath.startsWith('/uploads')) {
      return iconPath; // Return relative path, proxy will handle routing to backend
    }
    
    // Otherwise assume it's an asset path
    return iconPath;
  }
}

// Export singleton instance
const ceoService = new CeoService();
export default ceoService;

// Export types for use in components
export type { CeoData, CeoResponse, CeoUpdateResponse };
